import { useState } from "react";
import { ArrowLeft, ArrowRight, CheckCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Stepper from "../components/Stepper";
import LimitInput, { Limit } from "../components/LimitInput";
import MarkingScaleEditor, { MarkBand } from "../components/MarkingScaleEditor";
import { PRESETS } from "../components/ScalePresets";

interface RubricLevel {
  id: number;
  label: string;
  points: number;
  description: string;
}

interface Criterion {
  id: number;
  description: string;
  weight: number;
  levels: RubricLevel[];
}


interface AssessmentDraft {
  /* Basic */
  title: string;
  course: string;
  assignmentCode: string;
  dueDate: string;
  weighting: number; // percentage

  assessmentType: string; // e.g. Essay, Presentation, Exam
  collaboration: "individual" | "group";
  groupSize: number; // group size for group work, 0 = flexible/NA

  limit: Limit | null; // primary deliverable limit (null = none)

  totalMarks: number;
  markingBands: MarkBand[];
  markingNotes: string;

  /* Parts (optional) */
  parts: Part[];

  /* Learning design */
  objectives: string; // newline separated
  prerequisites: string;
  effortStudent: number; // hours
  effortInstructor: number; // hours

  /* Logistics */
  releaseDate: string;
  softDeadline: string;
  hardDeadline: string;
  peerReviewDeadline: string;
  submissionTypes: string[]; // e.g. ["pdf", "code"]

  coverSheetRequired: boolean;
  referencesMin: number;
  latePenalty: string;
  aiAllowed: boolean;
  bannedSources: string;
  qnaRequired: boolean; // for presentation discussion component
  formatting: {
    font: string;
    textSizePt: number;
    lineSpacing: number;
    referencingStyle: string;
  };

  /* Rubric */
  criteria: Criterion[];

  /* Resources */
  resources: string; // links / notes
  integrityPolicy: string;
}

interface Part {
  id: number;
  title: string;
  deliverableType: string;
  limit: Limit | null;
  weighting: number; // % or marks depending on rubric mode
  submissionType: string;
}

export default function NewAssessmentOnboardingPage() {
  const [step, setStep] = useState(0);
  const [data, setData] = useState<AssessmentDraft>({
    /* Basic */
    title: "",
    course: "",
    assignmentCode: "",
    dueDate: "",
    weighting: 10,

    assessmentType: "Essay",
    collaboration: "individual",
    groupSize: 0,
    limit: null,

    totalMarks: 100,
    markingBands: PRESETS[0].bands,
    markingNotes: "",

    parts: [],

    /* Learning */
    objectives: "",
    prerequisites: "",
    effortStudent: 2,
    effortInstructor: 1,

    /* Logistics */
    releaseDate: "",
    softDeadline: "",
    hardDeadline: "",
    peerReviewDeadline: "",
    submissionTypes: [],

    coverSheetRequired: false,
    referencesMin: 0,
    latePenalty: "",
    aiAllowed: false,
    bannedSources: "Wikipedia, Creative Spirits",
    qnaRequired: false,
    formatting: {
      font: "Times New Roman",
      textSizePt: 12,
      lineSpacing: 1.5,
      referencingStyle: "Harvard WesternSydU",
    },

    /* Rubric */
    criteria: [],

    /* Resources */
    resources: "",
    integrityPolicy: "",
  });

  const navigate = useNavigate();

  const steps = [
    "Basics",
    "Learning Design",
    "Logistics",
    "Rubric",
    "Resources",
    "Review",
  ];

  /* --------------------------------------------
   * Helpers
   * ------------------------------------------*/
  const next = () => setStep((s) => Math.min(s + 1, steps.length - 1));
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;

    // Coerce numbers for certain fields
    const numericFields = [
      "weighting",
      "effortStudent",
      "effortInstructor",
      "groupSize",
      "totalMarks",
      "referencesMin",
    ];

    setData((d) => ({
      ...d,
      [name]: numericFields.includes(name) ? Number(value) : value,
    }));
  };

  const addCriterion = () => {
    const defaultLevelsLabels = [
      "Exemplary",
      "Proficient",
      "Developing",
      "Beginning",
    ];
    const newLevels: RubricLevel[] = defaultLevelsLabels.map((label, idx) => ({
      id: Date.now() + idx,
      label,
      points: 4 - idx, // 4,3,2,1
      description: "",
    }));

    setData((d) => ({
      ...d,
      criteria: [
        ...d.criteria,
        { id: Date.now(), description: "", weight: 10, levels: newLevels },
      ],
    }));
  };

  const updateCriterion = (
    id: number,
    key: "description" | "weight",
    value: string,
  ) => {
    setData((d) => ({
      ...d,
      criteria: d.criteria.map((c) =>
        c.id === id ? { ...c, [key]: key === "weight" ? Number(value) : value } : c,
      ),
    }));
  };

  const updateLevel = (
    criterionId: number,
    levelId: number,
    key: "label" | "points" | "description",
    value: string,
  ) => {
    setData((d) => ({
      ...d,
      criteria: d.criteria.map((c) =>
        c.id === criterionId
          ? {
              ...c,
              levels: c.levels.map((l) =>
                l.id === levelId
                  ? {
                      ...l,
                      [key]: key === "points" ? Number(value) : value,
                    }
                  : l,
              ),
            }
          : c,
      ),
    }));
  };

  const addLevel = (criterionId: number) => {
    setData((d) => ({
      ...d,
      criteria: d.criteria.map((c) =>
        c.id === criterionId
          ? {
              ...c,
              levels: [
                ...c.levels,
                {
                  id: Date.now(),
                  label: "New level",
                  points: 0,
                  description: "",
                },
              ],
            }
          : c,
      ),
    }));
  };

  const removeLevel = (criterionId: number, levelId: number) => {
    setData((d) => ({
      ...d,
      criteria: d.criteria.map((c) =>
        c.id === criterionId
          ? { ...c, levels: c.levels.filter((l) => l.id !== levelId) }
          : c,
      ),
    }));
  };

  const removeCriterion = (id: number) => {
    setData((d) => ({ ...d, criteria: d.criteria.filter((c) => c.id !== id) }));
  };

  const handleSubmit = () => {
    console.log("Assessment created:", data);
    navigate("/app/marking");
  };

  /* --------------------------------------------
   * Per-step content
   * ------------------------------------------*/
  let content: JSX.Element | null = null;

  if (step === 0) {
    /* -------------------- BASICS ------------------- */
    content = (
      <div className="space-y-6">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Assignment code</label>
            <input
              type="text"
              name="assignmentCode"
              value={data.assignmentCode}
              onChange={handleChange}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
              placeholder="BIO101-Lab02"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Course</label>
            <input
              type="text"
              name="course"
              value={data.course}
              onChange={handleChange}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
              placeholder="BIO101"
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="aiAllowed"
            checked={data.aiAllowed}
            onChange={(e) => setData((d) => ({ ...d, aiAllowed: e.target.checked }))}
            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
          />
          <label htmlFor="aiAllowed" className="text-sm font-medium text-gray-700">
            Generative AI allowed
          </label>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="qnaRequired"
            checked={data.qnaRequired}
            onChange={(e) => setData((d) => ({ ...d, qnaRequired: e.target.checked }))}
            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
          />
          <label htmlFor="qnaRequired" className="text-sm font-medium text-gray-700">
            Q&amp;A / discussion segment is assessed
          </label>
        </div>

        {/* Assessment type & collaboration */}
        <div className="grid gap-4 sm:grid-cols-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Assessment type</label>
            <select
              name="assessmentType"
              value={data.assessmentType}
              onChange={handleChange}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
            >
              <option>Essay</option>
              <option>Report</option>
              <option>Presentation</option>
              <option>Exam</option>
              <option>Project</option>
              <option>Other</option>
            </select>
          </div>
          {data.collaboration === "group" && (
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Group size</label>
              <input
                type="number"
                name="groupSize"
                value={data.groupSize}
                onChange={handleChange}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                min={0}
              />
            </div>
          )}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Collaboration</label>
            <select
              name="collaboration"
              value={data.collaboration}
              onChange={handleChange}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
            >
              <option value="individual">Individual</option>
              <option value="group">Group</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Overall word limit</label>
            <input
              type="number"
              name="wordLimit"
              value={data.wordLimit}
              onChange={handleChange}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
              min={0}
            />
          </div>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Title</label>
          <input
            type="text"
            name="title"
            value={data.title}
            onChange={handleChange}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
            placeholder="Cell Metabolism Lab Report"
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Due date</label>
            <input
              type="date"
              name="dueDate"
              value={data.dueDate}
              onChange={handleChange}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Weighting (%)</label>
            <input
              type="number"
              name="weighting"
              value={data.weighting}
              onChange={handleChange}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
              min={0}
              max={100}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Total marks</label>
            <input
              type="number"
              name="totalMarks"
              value={data.totalMarks}
              onChange={handleChange}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
              min={0}
            />
          </div>
        </div>
      </div>
    );
  } else if (step === 1) {
    /* -------------- LEARNING DESIGN -------------- */
    content = (
      <div className="space-y-6">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Learning objectives</label>
          <textarea
            name="objectives"
            value={data.objectives}
            onChange={handleChange}
            rows={4}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
            placeholder="1. Explain the role of ATP in cellular respiration\n2. Evaluate experimental data on ..."
          />
          <p className="mt-1 text-xs text-gray-500">One objective per line.</p>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Prerequisite knowledge / skills</label>
          <textarea
            name="prerequisites"
            value={data.prerequisites}
            onChange={handleChange}
            rows={3}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
            placeholder="Students should already be familiar with ..."
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Estimated effort – student (hrs)</label>
            <input
              type="number"
              name="effortStudent"
              value={data.effortStudent}
              onChange={handleChange}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
              min={0}
              step={0.5}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Estimated effort – instructor (hrs)</label>
            <input
              type="number"
              name="effortInstructor"
              value={data.effortInstructor}
              onChange={handleChange}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
              min={0}
              step={0.5}
            />
          </div>
        </div>
      </div>
    );
  } else if (step === 2) {
    /* ------------------ LOGISTICS ----------------- */
    const toggleSubmissionType = (type: string) => {
      setData((d) => {
        const exists = d.submissionTypes.includes(type);
        return {
          ...d,
          submissionTypes: exists
            ? d.submissionTypes.filter((t) => t !== type)
            : [...d.submissionTypes, type],
        };
      });
    };

    const submissionOptions: { key: string; label: string }[] = [
      { key: "pdf", label: "File upload – PDF / DOCX" },
      { key: "code", label: "Git repo / ZIP" },
      { key: "text", label: "Rich text entry" },
      { key: "notebook", label: "Jupyter notebook" },
      { key: "video", label: "Video / Audio recording" },
      { key: "url", label: "External link" },
    ];

    content = (
      <div className="space-y-6">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Release date</label>
            <input
              type="datetime-local"
              name="releaseDate"
              value={data.releaseDate}
              onChange={handleChange}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Soft deadline</label>
            <input
              type="datetime-local"
              name="softDeadline"
              value={data.softDeadline}
              onChange={handleChange}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Hard deadline</label>
            <input
              type="datetime-local"
              name="hardDeadline"
              value={data.hardDeadline}
              onChange={handleChange}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Peer review closes</label>
            <input
              type="datetime-local"
              name="peerReviewDeadline"
              value={data.peerReviewDeadline}
              onChange={handleChange}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
            />
          </div>
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700">Allowed submission types</label>
          <div className="grid gap-2 sm:grid-cols-2">
            {submissionOptions.map((opt) => (
              <label key={opt.key} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={data.submissionTypes.includes(opt.key)}
                  onChange={() => toggleSubmissionType(opt.key)}
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                {opt.label}
              </label>
            ))}
          </div>
        </div>

        {/* Basic requirements */}
        <hr className="my-4" />
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Overall deliverable limit</label>
            <LimitInput
              limit={
                data.limit ?? { type: "word", value: 0, allowancePct: 0 }
              }
              onChange={(lim) => setData((d) => ({ ...d, limit: lim }))}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="mb-1 block text-sm font-medium text-gray-700">Min. references</label>
            <input
              type="number"
              name="referencesMin"
              value={data.referencesMin}
              onChange={handleChange}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
              min={0}
            />
          </div>
          <div className="flex items-end gap-2 pt-1">
            <input
              type="checkbox"
              id="coverSheetRequired"
              checked={data.coverSheetRequired}
              onChange={(e) =>
                setData((d) => ({ ...d, coverSheetRequired: e.target.checked }))
              }
              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
            />
            <label htmlFor="coverSheetRequired" className="text-sm font-medium text-gray-700">
              Cover sheet required
            </label>
          </div>
        </div>

      {/* Banned / discouraged sources */}
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700">Banned / discouraged sources</label>
        <input
          type="text"
          name="bannedSources"
          value={data.bannedSources}
          onChange={handleChange}
          className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
          placeholder="e.g. Wikipedia, Creative Spirits"
        />
      </div>

        <div className="grid gap-4 sm:grid-cols-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Font</label>
            <input
              type="text"
              value={data.formatting.font}
              onChange={(e) =>
                setData((d) => ({
                  ...d,
                  formatting: { ...d.formatting, font: e.target.value },
                }))
              }
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Text size (pt)</label>
            <input
              type="number"
              value={data.formatting.textSizePt}
              onChange={(e) =>
                setData((d) => ({
                  ...d,
                  formatting: { ...d.formatting, textSizePt: Number(e.target.value) },
                }))
              }
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
              min={6}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Line spacing</label>
            <input
              type="number"
              step={0.1}
              value={data.formatting.lineSpacing}
              onChange={(e) =>
                setData((d) => ({
                  ...d,
                  formatting: { ...d.formatting, lineSpacing: Number(e.target.value) },
                }))
              }
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
              min={1}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Referencing style</label>
            <input
              type="text"
              value={data.formatting.referencingStyle}
              onChange={(e) =>
                setData((d) => ({
                  ...d,
                  formatting: { ...d.formatting, referencingStyle: e.target.value },
                }))
              }
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
            />
          </div>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Late penalty / other policies</label>
          <input
            type="text"
            name="latePenalty"
            value={data.latePenalty}
            onChange={handleChange}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
            placeholder="10% penalty per day late"
          />
        </div>

        {/* Part breakdown (optional) */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-gray-800">Optional: Parts breakdown</h3>
          {data.parts.map((p) => (
            <div key={p.id} className="space-y-4 rounded-md border border-gray-200 p-4">
              {/* header */}
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium truncate max-w-[70%]">
                  {p.title || "(Untitled part)"}
                </h4>
                <button
                  onClick={() =>
                    setData((d) => ({
                      ...d,
                      parts: d.parts.filter((q) => q.id !== p.id),
                    }))
                  }
                  className="text-xs text-red-600 hover:underline"
                >
                  Remove
                </button>
              </div>

              <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3">
                {/* title edit */}
                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-700">Title</label>
                  <input
                    type="text"
                    value={p.title}
                    onChange={(e) =>
                      setData((d) => ({
                        ...d,
                        parts: d.parts.map((q) =>
                          q.id === p.id ? { ...q, title: e.target.value } : q,
                        ),
                      }))
                    }
                    className="w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-primary focus:ring-primary"
                  />
                </div>

                {/* type */}
                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-700">Type</label>
                  <input
                    type="text"
                    value={p.deliverableType}
                    onChange={(e) =>
                      setData((d) => ({
                        ...d,
                        parts: d.parts.map((q) =>
                          q.id === p.id ? { ...q, deliverableType: e.target.value } : q,
                        ),
                      }))
                    }
                    className="w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-primary focus:ring-primary"
                  />
                </div>

                {/* weight */}
                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-700">Weight / Marks</label>
                  <input
                    type="number"
                    value={p.weighting}
                    min={0}
                    onChange={(e) =>
                      setData((d) => ({
                        ...d,
                        parts: d.parts.map((q) =>
                          q.id === p.id ? { ...q, weighting: Number(e.target.value) } : q,
                        ),
                      }))
                    }
                    className="w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-primary focus:ring-primary"
                  />
                </div>

                {/* limit */}
                <div className="space-y-1 md:col-span-2">
                  <label className="block text-xs font-medium text-gray-700">Limit</label>
                  <LimitInput
                    limit={p.limit ?? { type: "word", value: 0, allowancePct: 0 }}
                    onChange={(lim) =>
                      setData((d) => ({
                        ...d,
                        parts: d.parts.map((q) =>
                          q.id === p.id ? { ...q, limit: lim } : q,
                        ),
                      }))
                    }
                  />
                </div>

                {/* submission */}
                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-700">Submission type</label>
                  <select
                    value={p.submissionType}
                    onChange={(e) =>
                      setData((d) => ({
                        ...d,
                        parts: d.parts.map((q) =>
                          q.id === p.id ? { ...q, submissionType: e.target.value } : q,
                        ),
                      }))
                    }
                    className="w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-primary focus:ring-primary"
                  >
                    <option value="">Select…</option>
                    {submissionOptions.map((opt) => (
                      <option key={opt.key} value={opt.key}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          ))}
          <button
            onClick={() =>
              setData((d) => ({
                ...d,
                parts: [
                  ...d.parts,
                  {
                    id: Date.now(),
                    title: "New part",
                    deliverableType: "Part",
                    limit: { type: "word", value: 0, allowancePct: 0 },
                    weighting: 0,
                    submissionType: "",
                  },
                ],
              }))
            }
            className="rounded-md border border-dashed border-primary px-3 py-1 text-xs font-medium text-primary hover:bg-primary/10"
          >
            + Add part
          </button>
        </div>
      </div>
    );
  } else if (step === 3) {
    /* ------------------- RUBRIC ------------------- */
    content = (
      <div className="space-y-8">
        {/* Rubric settings */}
        <div className="space-y-4 rounded-md border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium">Marking bands</h4>
            <select
              className="rounded-md border-gray-300 text-xs"
              onChange={(e) => {
                const preset = PRESETS.find((p) => p.id === e.target.value);
                if (preset) setData((d) => ({ ...d, markingBands: preset.bands }));
              }}
            >
              <option value="">Load preset…</option>
              {PRESETS.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>

          <MarkingScaleEditor
            bands={data.markingBands}
            onChange={(b) => setData((d) => ({ ...d, markingBands: b }))}
          />

          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700">Notes (optional)</label>
            <textarea
              rows={2}
              value={data.markingNotes}
              onChange={(e) => setData((d) => ({ ...d, markingNotes: e.target.value }))}
              className="w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-primary focus:ring-primary"
            />
          </div>
        </div>

        {data.criteria.map((c) => (
          <div key={c.id} className="space-y-4 rounded-md bg-gray-50 p-4">
            {/* Criterion header */}
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
              <input
                type="text"
                value={c.description}
                onChange={(e) => updateCriterion(c.id, "description", e.target.value)}
                placeholder="Criterion (e.g. Analysis, Presentation)"
                className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
              />
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={c.weight}
                  onChange={(e) => updateCriterion(c.id, "weight", e.target.value)}
                  className="w-24 rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                  min={0}
                  max={100}
                />
                <span className="text-sm text-gray-600">% weight</span>
              </div>
              <button
                onClick={() => removeCriterion(c.id)}
                className="ml-auto text-sm text-red-600 hover:underline"
              >
                Remove criterion
              </button>
            </div>

            {/* Levels table */}
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left">
                    <th className="px-2 py-1 font-medium text-gray-700">Level</th>
                    <th className="px-2 py-1 font-medium text-gray-700">Points</th>
                    <th className="px-2 py-1 font-medium text-gray-700">Descriptor</th>
                    <th className="px-2 py-1" />
                  </tr>
                </thead>
                <tbody>
                  {c.levels.map((l) => (
                    <tr key={l.id} className="border-b border-gray-100">
                      <td className="px-2 py-1">
                        <input
                          type="text"
                          value={l.label}
                          onChange={(e) =>
                            updateLevel(c.id, l.id, "label", e.target.value)
                          }
                          className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                        />
                      </td>
                      <td className="px-2 py-1">
                        <input
                          type="number"
                          value={l.points}
                          onChange={(e) =>
                            updateLevel(c.id, l.id, "points", e.target.value)
                          }
                          className="w-20 rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                          min={0}
                        />
                      </td>
                      <td className="px-2 py-1">
                        <textarea
                          value={l.description}
                          onChange={(e) =>
                            updateLevel(c.id, l.id, "description", e.target.value)
                          }
                          rows={2}
                          className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                        />
                      </td>
                      <td className="px-2 py-1 text-right">
                        {c.levels.length > 1 && (
                          <button
                            onClick={() => removeLevel(c.id, l.id)}
                            className="text-xs text-red-600 hover:underline"
                          >
                            Remove
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <button
              onClick={() => addLevel(c.id)}
              className="mt-2 rounded-md border border-dashed border-primary px-3 py-1 text-xs font-medium text-primary hover:bg-primary/10"
            >
              + Add level
            </button>
          </div>
        ))}

        <button
          onClick={addCriterion}
          className="rounded-md border border-dashed border-primary px-4 py-2 text-sm font-medium text-primary hover:bg-primary/10"
        >
          + Add criterion
        </button>
      </div>
    );
  } else if (step === 4) {
    /* ----------------- RESOURCES ------------------ */
    content = (
      <div className="space-y-6">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Links / Files / Instructions</label>
          <textarea
            name="resources"
            value={data.resources}
            onChange={handleChange}
            rows={6}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
            placeholder="Paste Google Drive links, starter repo URL, dataset location…"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Academic integrity statement</label>
          <textarea
            name="integrityPolicy"
            value={data.integrityPolicy}
            onChange={handleChange}
            rows={4}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
            placeholder="Specify allowed collaboration, AI tool usage, citation style, etc."
          />
        </div>
      </div>
    );
  } else if (step === 5) {
    /* ------------------- REVIEW ------------------- */
    const totalWeight = data.criteria.reduce((sum, c) => sum + c.weight, 0);
    content = (
      <div className="space-y-4 text-sm">
        <h3 className="text-lg font-medium text-gray-800">Review summary</h3>
        <ul className="space-y-1">
          <li>
            <strong>Code:</strong> {data.assignmentCode || "—"}
          </li>
          <li>
            <strong>Title:</strong> {data.title || "—"}
          </li>
          <li>
            <strong>Course:</strong> {data.course || "—"}
          </li>
          <li>
            <strong>Due:</strong> {data.dueDate || "—"}
          </li>
          <li>
            <strong>Weighting:</strong> {data.weighting}%
          </li>
          <li>
            <strong>Learning objectives:</strong> {data.objectives.split("\n").length} lines
          </li>
          <li>
            <strong>Criteria:</strong> {data.criteria.length} items (total {totalWeight}%)
          </li>
          <li>
            <strong>Submission types:</strong> {data.submissionTypes.join(", ") || "—"}
          </li>
        </ul>
      </div>
    );
  }

  /* --------------------------------------------
   * UI shell
   * ------------------------------------------*/
  return (
    <div className="mx-auto max-w-3xl space-y-8">
      {/* Header */}
      <header className="text-center">
        <h1 className="mb-2 text-3xl font-semibold text-primary">New assignment onboarding</h1>
        <p className="text-sm text-gray-600">Follow the steps to capture all pedagogical and logistical details.</p>
      </header>

      {/* Stepper */}
      <Stepper steps={steps} currentStep={step} />

      {/* Step content */}
      <section className="rounded-xl bg-white p-6 shadow-sm">{content}</section>

      {/* Controls */}
      <div className="flex justify-between">
        <button
          onClick={step === 0 ? () => navigate(-1) : prev}
          className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <ArrowLeft size={16} /> {step === 0 ? "Cancel" : "Back"}
        </button>

        {step < steps.length - 1 ? (
          <button
            onClick={next}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white shadow hover:bg-primary/90"
          >
            Next <ArrowRight size={16} />
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            className="inline-flex items-center gap-2 rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-green-700"
          >
            <CheckCircle size={16} /> Create assessment
          </button>
        )}
      </div>
    </div>
  );
}
